/**
 * Custom hook for opening Google Picker via central auth domain popup
 * Handles cross-domain communication using postMessage API
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
    GooglePickerResult,
    GooglePickerMessage,
    isValidOrigin,
    getMainDomain,
    openCenteredPopup
} from "./google-picker-message.util";

interface UseGooglePickerPopupOptions {
    /** Callback when picker selection is successful */
    onSuccess?: (url: string, accessToken: string) => void;
    /** Callback when picker is cancelled */
    onCancel?: () => void;
    /** Callback on error */
    onError?: (error: string) => void;
}

interface UseGooglePickerPopupReturn {
    /** Opens the Google Picker popup */
    openPicker: () => void;
    /** Whether the picker popup is currently open */
    isOpen: boolean;
    /** The last result from the picker */
    result: GooglePickerResult | null;
    /** Loading state while waiting for picker */
    isLoading: boolean;
}

/**
 * Gets the Google Picker URL on the auth domain
 */
const getPickerUrl = (): string => {
    const hostname = window.location.hostname;

    // Handle localhost development
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return `http://localhost:${window.location.port || 3000}/google-picker`;
    }

    const pickerDomain = process.env.REACT_APP_LOGIN_DOMAIN;
    if (!pickerDomain) {
        throw new Error("REACT_APP_LOGIN_DOMAIN environment variable not set");
    }

    return `${pickerDomain}/google-picker`;
};

/**
 * Hook for managing Google Picker popup with cross-domain communication
 */
export const useGooglePickerPopup = (
    options: UseGooglePickerPopupOptions = {}
): UseGooglePickerPopupReturn => {
    const { onSuccess, onCancel, onError } = options;

    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<GooglePickerResult | null>(null);
    const popupRef = useRef<Window | null>(null);
    const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
            }
        };
    }, []);

    // Handle incoming messages from the picker popup
    const handleMessage = useCallback(
        (event: MessageEvent) => {
            console.log("useGooglePickerPopup: Received message from origin:", event.origin);

            // Validate origin
            const mainDomain = getMainDomain();
            if (!isValidOrigin(event.origin, [mainDomain])) {
                console.log("useGooglePickerPopup: Origin validation failed, ignoring message");
                return;
            }

            const message = event.data as GooglePickerMessage;

            if (!message || !message.type) {
                console.log("useGooglePickerPopup: Invalid message format, ignoring");
                return;
            }

            console.log("useGooglePickerPopup: Valid message received, type:", message.type);

            switch (message.type) {
                case "GOOGLE_PICKER_READY":
                    console.log("useGooglePickerPopup: Picker is ready");
                    setIsLoading(false);
                    break;

                case "GOOGLE_PICKER_RESULT":
                    console.log("useGooglePickerPopup: Got picker result", message.payload);
                    if (message.payload) {
                        setResult(message.payload);

                        if (message.payload.success && message.payload.url) {
                            console.log(
                                "useGooglePickerPopup: Calling onSuccess with URL:",
                                message.payload.url
                            );
                            onSuccess?.(message.payload.url, message.payload.accessToken || "");
                        } else if (message.payload.error) {
                            console.log(
                                "useGooglePickerPopup: Calling onError:",
                                message.payload.error
                            );
                            onError?.(message.payload.error);
                        }
                    }
                    setIsOpen(false);
                    setIsLoading(false);
                    break;

                case "GOOGLE_PICKER_CANCEL":
                    console.log("useGooglePickerPopup: Picker was cancelled");
                    onCancel?.();
                    setIsOpen(false);
                    setIsLoading(false);
                    break;
            }
        },
        [onSuccess, onCancel, onError]
    );

    // Set up message listener only when popup is open
    useEffect(() => {
        if (!isOpen) return;
        console.log("useGooglePickerPopup: Setting up message listener");
        window.addEventListener("message", handleMessage);
        return () => {
            console.log("useGooglePickerPopup: Removing message listener");
            window.removeEventListener("message", handleMessage);
        };
    }, [handleMessage, isOpen]);

    // Monitor popup closure (user closing window manually)
    useEffect(() => {
        if (isOpen && popupRef.current) {
            checkIntervalRef.current = setInterval(() => {
                if (popupRef.current?.closed) {
                    setIsOpen(false);
                    setIsLoading(false);
                    if (checkIntervalRef.current) {
                        clearInterval(checkIntervalRef.current);
                        checkIntervalRef.current = null;
                    }
                }
            }, 500);
        }

        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
            }
        };
    }, [isOpen]);

    const openPicker = useCallback(() => {
        try {
            const baseUrl = getPickerUrl();
            // Pass the current origin so the popup knows where to send results
            const url = `${baseUrl}?returnOrigin=${encodeURIComponent(window.location.origin)}`;

            const popup = openCenteredPopup(url, "GooglePickerPopup", 900, 650);

            if (popup) {
                popupRef.current = popup;
                setIsOpen(true);
                setIsLoading(true);
                setResult(null);
            } else {
                onError?.("Popup was blocked. Please allow popups for this site.");
            }
        } catch (error) {
            onError?.(error instanceof Error ? error.message : "Failed to open picker");
        }
    }, [onError]);

    return {
        openPicker,
        isOpen,
        result,
        isLoading
    };
};

export default useGooglePickerPopup;
